import React, { useEffect, useState, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { Reading, Device } from '../types';
import api, { getWebSocketUrl } from '../services/api';

interface MappingInfo {
  label?: string;
  unit?: string;
  type?: 'number' | 'string';
}

export default function LiveReadings({ siteId }: { siteId?: string }) {
  const isMobile = useIsMobile();
  const [readings, setReadings] = useState<Reading[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasWsData = useRef(false);

  // When a site filter is active, only show readings from devices in that site.
  const visibleReadings = siteId
    ? readings.filter((r) => devices.find((d) => d.id === r.device_id)?.site_id === siteId)
    : readings;

  // Load devices so we can resolve metric labels/units from parser_config.
  // Refetch periodically so mapping edits reflect quickly in Live Sensors.
  useEffect(() => {
    async function loadDevices() {
      try {
        const res = await api.get('/devices');
        setDevices(res.data);
      } catch {
        // ignore
      }
    }
    loadDevices();
    const interval = setInterval(loadDevices, 10000);
    return () => clearInterval(interval);
  }, []);

  // WebSocket connection
  useEffect(() => {
    const wsUrl = getWebSocketUrl('/ws');

    function connect() {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setError('');
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        hasWsData.current = false;
        // Auto-reconnect after 3s
        setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        setError('WebSocket connection failed');
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'readings') {
            hasWsData.current = true;
            setReadings(msg.data);
          }
        } catch {
          // ignore
        }
      };
    }

    connect();

    return () => {
      wsRef.current?.close();
    };
  }, []);

  // Polling fallback — ONLY when WebSocket is not connected AND no WS data received
  useEffect(() => {
    async function poll() {
      try {
        const res = await api.get('/analytics/readings?hours=1&limit=500');
        if (res.data && res.data.length > 0) {
          const merged = mergeLatestReadings(res.data);
          // Only update if WebSocket hasn't provided data
          if (!hasWsData.current) {
            setReadings(merged);
          }
        }
      } catch {
        // silently fail
      }
    }

    // Poll immediately and every 5s, but respect WS data
    poll();
    pollRef.current = setInterval(() => {
      if (!hasWsData.current) poll();
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function mergeLatestReadings(rows: Reading[]): Reading[] {
    const byDevice: Record<string, Reading> = {};
    for (const r of rows) {
      const id = r.device_id;
      if (!byDevice[id]) {
        byDevice[id] = { ...r };
        continue;
      }
      const merged = byDevice[id];
      if (new Date(r.time) > new Date(merged.time)) {
        merged.time = r.time;
      }
      if (r.temperature != null && merged.temperature == null) merged.temperature = r.temperature;
      if (r.humidity != null && merged.humidity == null) merged.humidity = r.humidity;
      if (r.pressure != null && merged.pressure == null) merged.pressure = r.pressure;
      if (r.battery != null && merged.battery == null) merged.battery = r.battery;
      if (r.metadata) {
        merged.metadata = merged.metadata || {};
        for (const [key, value] of Object.entries(r.metadata)) {
          if (!(key in merged.metadata)) {
            merged.metadata[key] = value;
          }
        }
      }
    }
    return Object.values(byDevice);
  }

  return (
    <div style={{ ...styles.card, padding: isMobile ? '1rem' : '1.5rem' }} className="glass-card">
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Live Sensors</h3>
          <p style={styles.subtitle}>
            {visibleReadings.length > 0
              ? `${visibleReadings.length} active sensor${visibleReadings.length > 1 ? 's' : ''}`
              : 'No sensors currently active'}
          </p>
        </div>
        <div style={{ ...styles.status, background: connected ? 'rgba(45,212,168,0.12)' : 'rgba(232,96,44,0.12)' }}>
          <span style={{ ...styles.dot, background: connected ? '#2DD4A8' : '#e8602c', boxShadow: connected ? '0 0 8px #2DD4A8' : '0 0 8px #e8602c' }} />
          <span style={{ color: connected ? '#2DD4A8' : '#e8602c', fontSize: '0.75rem', fontWeight: 600 }}>
            {connected ? 'LIVE' : 'POLLING'}
          </span>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: isMobile ? '0.625rem' : '0.875rem' }}>
        {visibleReadings.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>📡</div>
            <p>No active sensors</p>
            <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
              {connected ? 'All devices are offline. Start the simulator to see data.' : 'Waiting for connection...'}
            </p>
          </div>
        )}
        {visibleReadings.map((r) => (
          <div key={r.device_id} style={styles.item}>
            <div style={styles.itemHeader}>
              <span style={styles.deviceName}>{r.device_name || r.device_id.slice(0, 8)}</span>
              <span style={styles.timestamp}>{new Date(r.time).toLocaleTimeString()}</span>
            </div>
            <div style={{ ...styles.metrics, gap: isMobile ? '0.5rem' : '0.75rem' }}>
              {r.temperature !== undefined && r.temperature !== null && (
                <Metric label="Temp" value={r.temperature} unit="°C" color="#F5A623" />
              )}
              {r.humidity !== undefined && r.humidity !== null && (
                <Metric label="Humidity" value={r.humidity} unit="%" color="#3b82f6" />
              )}
              {r.pressure !== undefined && r.pressure !== null && (
                <Metric label="Pressure" value={r.pressure} unit="hPa" color="#a855f7" />
              )}
              {r.battery !== undefined && r.battery !== null && (
                <Metric label="Battery" value={r.battery} unit="%" color="#2DD4A8" />
              )}
              {r.metadata &&
                getOrderedMetadataKeys(r.metadata, devices, r.device_id).map((key) => {
                  const value = r.metadata![key];
                  const mapping = getDeviceMapping(devices, r.device_id, key);
                  return (
                    <Metric
                      key={key}
                      label={mapping.label || formatMetricLabel(key)}
                      value={value}
                      unit={mapping.unit || ''}
                      color="#e8602c"
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, unit, color }: { label: string; value?: any; unit: string; color: string }) {
  const displayValue =
    value !== undefined && value !== null
      ? typeof value === 'number'
        ? value.toFixed(1)
        : String(value)
      : '—';
  return (
    <div style={styles.metricBox}>
      <div style={{ ...styles.metricValue, color }}>
        {displayValue}
        {typeof value === 'number' && unit && <span style={styles.metricUnit}>{unit}</span>}
      </div>
      <div style={styles.metricLabel}>{label}</div>
    </div>
  );
}

function getDeviceMapping(devices: Device[], deviceId: string, field: string): MappingInfo {
  const device = devices.find((d) => d.id === deviceId);
  if (!device?.parser_config?.mappings?.[field]) return {};
  const m = device.parser_config.mappings[field];
  return {
    label: m.label,
    unit: m.unit,
    type: m.type,
  };
}

function getOrderedMetadataKeys(
  metadata: Record<string, any>,
  devices: Device[],
  deviceId: string
): string[] {
  const device = devices.find((d) => d.id === deviceId);
  const mappings = device?.parser_config?.mappings;

  // For virtual/gateway devices with explicit mappings, only show mapped keys
  // in the order the user configured them. This prevents old/removed field
  // names from lingering after mapping edits.
  if (mappings && Object.keys(mappings).length > 0) {
    return Object.keys(mappings).filter((k) => k in metadata);
  }

  // For other devices, show all metadata keys (except api_key) alphabetically
  return Object.keys(metadata)
    .filter((k) => k !== 'api_key')
    .sort((a, b) => a.localeCompare(b));
}

function formatMetricLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    padding: '1.5rem',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.25rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    borderRadius: '8px',
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    animation: 'pulse 2s infinite',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '0.875rem',
  },
  empty: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '2rem',
    color: 'var(--text-muted)',
    fontSize: '0.9rem',
  },
  emptyIcon: {
    fontSize: '2rem',
    marginBottom: '0.5rem',
    opacity: 0.5,
  },
  error: {
    gridColumn: '1 / -1',
    color: '#e8602c',
    background: 'rgba(232,96,44,0.1)',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.8rem',
    marginBottom: '0.75rem',
  },
  item: {
    background: 'rgba(11, 13, 20, 0.5)',
    border: '1px solid rgba(255,255,255,0.04)',
    borderRadius: '10px',
    padding: '1rem',
    transition: 'border-color 0.2s',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  deviceName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    background: 'rgba(255,255,255,0.04)',
    padding: '0.125rem 0.5rem',
    borderRadius: '4px',
  },
  timestamp: {
    fontSize: '0.65rem',
    color: 'var(--text-muted)',
  },
  metrics: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  metricBox: {
    flex: 1,
    textAlign: 'center',
  },
  metricValue: {
    fontSize: '1.15rem',
    fontWeight: 700,
  },
  metricUnit: {
    fontSize: '0.7rem',
    fontWeight: 500,
    marginLeft: '2px',
    opacity: 0.7,
  },
  metricLabel: {
    fontSize: '0.65rem',
    color: 'var(--text-muted)',
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
};
